import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import { Modal, RECEIVABLES_API } from "./common";

const TALLY_API = `${RECEIVABLES_API}/collections/tally`;

const KEY_LABELS = {
  Date: "Collection date",
  VoucherTypeName: "Voucher type (per payment mode)",
  CollectionId: "Voucher no (RCT + id)",
  VoucherNum: "Receipt number",
  DrLedger: "Dr ledger (per payment mode)",
  CustomerName: "Customer (Cr ledger)",
  CollectionAmount: "Amount",
  InvoiceNo: "Bill number",
  CollType: "Payment mode",
  RefNumber: "Instrument / reference no",
  ChequeDate: "Instrument date",
  BankName: "Bank name",
  SalesPersonName: "Collected by (name)",
  AppUserId: "Collected by (role)",
};

const MODES = ["CASH", "UPI", "CHEQUE", "EFUND_TRANSFER", "DISCOUNT", "EXPIRED", "FC_PAYOUT"];

/**
 * Layout of the Tally export: the columns in order (each bound to a value, or a fixed text with CONST=...) and, per payment mode,
 * the Tally voucher type and debit ledger. A header with "\n" prints over two header rows.
 */
const TallySettingsModal = ({ onClose }) => {
  const [config, setConfig] = useState(null);
  const [keys, setKeys] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([api.get(`${TALLY_API}/config`), api.get(`${TALLY_API}/keys`)])
      .then(([cfg, ks]) => {
        setConfig({ ...cfg, paymentModes: { ...Object.fromEntries(MODES.map((m) => [m, { voucherTypeName: "", drLedger: "" }])), ...(cfg.paymentModes || {}) } });
        setKeys(Array.isArray(ks) ? ks : []);
      })
      .catch((error) => toast.error(error.message || "Could not load the Tally settings"));
  }, []);

  if (!config) return null;

  const setColumn = (index, patch) => setConfig((prev) => ({ ...prev, columns: prev.columns.map((c, i) => (i === index ? { ...c, ...patch } : c)) }));
  const move = (index, step) =>
    setConfig((prev) => {
      const columns = [...prev.columns];
      const target = index + step;
      if (target < 0 || target >= columns.length) return prev;
      [columns[index], columns[target]] = [columns[target], columns[index]];
      return { ...prev, columns };
    });
  const setMode = (mode, patch) => setConfig((prev) => ({ ...prev, paymentModes: { ...prev.paymentModes, [mode]: { ...prev.paymentModes[mode], ...patch } } }));

  const save = async () => {
    setSaving(true);
    try {
      const saved = await api.put(`${TALLY_API}/config`, config);
      setConfig((prev) => ({ ...prev, ...saved, paymentModes: { ...prev.paymentModes, ...(saved.paymentModes || {}) } }));
      toast.success("Tally export settings saved");
      onClose();
    } catch (error) {
      toast.error(error.message || "Could not save the Tally settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Tally Export Settings" onClose={onClose} maxWidth="960px">
      <div className="page-subtitle" style={{ marginBottom: 8 }}>
        {config.saved ? "Saved layout." : "Default layout — save to keep your own."} The export always ends with a "Cheque Status" column (Yes for a
        post-dated cheque). Visit markers, credit and debit notes are left out; cheque, UPI and e-fund receipts of one customer with one reference are
        one voucher (their total on the first row).
      </div>
      <h4 style={{ fontWeight: 600 }}>Columns</h4>
      <div className="table-wrapper" style={{ marginTop: 4, maxHeight: "40vh", overflowY: "auto" }}>
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Value</th>
              <th>Fixed text (CONST)</th>
              <th>Header in Excel</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {config.columns.map((column, index) => {
              const constant = column.key.startsWith("CONST=");
              return (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>
                    <select value={constant ? "CONST" : column.key} onChange={(e) => setColumn(index, { key: e.target.value === "CONST" ? "CONST=" : e.target.value })}>
                      {keys.map((k) => (
                        <option key={k} value={k}>
                          {KEY_LABELS[k] || k}
                        </option>
                      ))}
                      <option value="CONST">Fixed text</option>
                    </select>
                  </td>
                  <td>
                    {constant ? (
                      <input value={column.key.slice("CONST=".length)} onChange={(e) => setColumn(index, { key: `CONST=${e.target.value}` })} />
                    ) : (
                      "-"
                    )}
                  </td>
                  <td>
                    <input value={column.header} onChange={(e) => setColumn(index, { header: e.target.value })} />
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <button type="button" className="secondary" onClick={() => move(index, -1)} title="Move up">
                      ↑
                    </button>{" "}
                    <button type="button" className="secondary" onClick={() => move(index, 1)} title="Move down">
                      ↓
                    </button>{" "}
                    <button
                      type="button"
                      className="secondary"
                      onClick={() => setConfig((prev) => ({ ...prev, columns: prev.columns.filter((_, i) => i !== index) }))}
                      title="Remove"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <button
        type="button"
        className="secondary"
        style={{ marginTop: 8 }}
        onClick={() => setConfig((prev) => ({ ...prev, columns: [...prev.columns, { key: "CustomerName", header: "Party" }] }))}
      >
        + Add column
      </button>

      <h4 style={{ fontWeight: 600, marginTop: 16 }}>Per payment mode</h4>
      <div className="table-wrapper" style={{ marginTop: 4 }}>
        <table>
          <thead>
            <tr>
              <th>Payment Mode</th>
              <th>Voucher Type</th>
              <th>Dr Ledger</th>
            </tr>
          </thead>
          <tbody>
            {Object.keys(config.paymentModes).map((mode) => (
              <tr key={mode}>
                <td>{mode.replace(/_/g, " ")}</td>
                <td>
                  <input value={config.paymentModes[mode]?.voucherTypeName || ""} onChange={(e) => setMode(mode, { voucherTypeName: e.target.value })} />
                </td>
                <td>
                  <input value={config.paymentModes[mode]?.drLedger || ""} onChange={(e) => setMode(mode, { drLedger: e.target.value })} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <label style={{ display: "inline-flex", gap: 6, alignItems: "center", marginTop: 10 }}>
        <input type="checkbox" checked={!!config.upiBankAsLedger} onChange={(e) => setConfig((prev) => ({ ...prev, upiBankAsLedger: e.target.checked }))} />
        Use the bank name as the Dr ledger of UPI receipts
      </label>
      <div className="modal-actions">
        <button type="button" className="secondary" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="primary" onClick={save} disabled={saving}>
          {saving ? "Saving..." : "Save"}
        </button>
      </div>
    </Modal>
  );
};

export default TallySettingsModal;
